import axios from 'axios';

const DEFAULT_TIMEOUT_MS = 60000;

export class ChartingHttpError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'ChartingHttpError';
    this.status = options.status || null;
    this.code = options.code || '';
    this.cause = options.cause;
  }
}

const chartingClient = axios.create({
  baseURL: process.env.VUE_APP_API || '',
  timeout: DEFAULT_TIMEOUT_MS,
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  }
});

chartingClient.interceptors.request.use(config => {
  const token = localStorage.getItem('user_access_token');

  if (token) {
    config.headers = config.headers || {};
    config.headers.authorization = `Bearer ${token}`;
  }

  return config;
});

function extractErrorMessage(error) {
  const responseData = error && error.response && error.response.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData.trim();
  }

  if (responseData && typeof responseData === 'object') {
    return responseData.message || responseData.error || '';
  }

  return error && error.message ? error.message : '';
}

function wrapRequestError(error) {
  if (axios.isCancel(error)) {
    return new ChartingHttpError('La solicitud del graficador se ha cancelado.', {
      code: 'REQUEST_CANCELED',
      cause: error
    });
  }

  return new ChartingHttpError(
    extractErrorMessage(error) || 'No se pudo completar la solicitud del graficador.',
    {
      status: error && error.response ? error.response.status : null,
      code: 'REQUEST_FAILED',
      cause: error
    }
  );
}

export async function getChartingResource(path, config = {}) {
  try {
    const response = await chartingClient.get(path, config);
    return response.data;
  } catch (error) {
    throw wrapRequestError(error);
  }
}

export async function postChartingResource(path, payload = null, config = {}) {
  try {
    const response = await chartingClient.post(path, payload, config);
    return response.data;
  } catch (error) {
    throw wrapRequestError(error);
  }
}

export function buildChartingFormBody(payload = {}, options = {}) {
  const params = new URLSearchParams();
  const arrayMode = options.arrayMode || 'repeat';

  Object.keys(payload || {}).forEach(key => {
    const value = payload[key];

    if (value === null || value === undefined || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      const parameterKey = arrayMode === 'brackets' ? `${key}[]` : key;
      value.forEach(item => {
        if (item !== null && item !== undefined && item !== '') {
          params.append(parameterKey, String(item));
        }
      });
      return;
    }

    params.append(key, String(value));
  });

  return params.toString();
}

export async function postChartingForm(path, payload = {}, options = {}) {
  return postChartingResource(path, buildChartingFormBody(payload, options.formOptions), {
    ...(options.config || {}),
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...((options.config && options.config.headers) || {})
    }
  });
}

export function createChartingCancelSource() {
  return axios.CancelToken.source();
}

export function isChartingRequestCanceled(error) {
  return Boolean(error && error.code === 'REQUEST_CANCELED');
}
